/**
 * Which sites a client may see in the portal.
 *
 * `PUT /api/users/:id/sites` has existed and been tested since the grants work
 * landed; nothing in the product could reach it. This is that screen.
 *
 * ── The failure this screen is shaped around ─────────────────────────────
 *
 * 🔴 The predecessor's worst interface failure was a silent empty ACL behind a
 * green toast: the list saved, the screen said so, and the customer logged in to
 * a blank portal. The sentence that matters in that account is **"nobody had
 * decided to remove their access — the form had simply submitted what it was
 * showing."**
 *
 * So the guard cannot only be a confirmation on the empty case. A form that
 * shows a set of checkboxes and a Save button will always be able to submit
 * something nobody chose, and asking "are you sure?" about the destination
 * tells the operator nothing about the journey. **This screen states the
 * CHANGE, not the state** — what is being added, what is being taken away, by
 * name — and it is the diff that is confirmed. An operator who did not mean it
 * is reading a list of removals they did not expect, which is a question they
 * can actually answer.
 *
 * The empty-set confirmation still exists, because the server requires it and
 * because losing every site is categorically worse than losing one. It is the
 * second guard, not the only one.
 *
 * ── Why the toast is not the evidence ────────────────────────────────────
 *
 * `Toast`'s own docstring says it: a toast confirms what a screen ALREADY
 * shows. On success this re-renders from the server's reply — the set it
 * actually stored, not the set we sent — so the confirmation a person reads is
 * the database's answer rather than our optimism.
 *
 * ── The retired-site trap ────────────────────────────────────────────────
 *
 * ⚠️ `GET /api/sites` excludes retired sites. A client granted a site that was
 * later retired therefore has a grant this screen would not draw, and a
 * replace-the-set save built from the drawn checkboxes alone would drop it
 * without anyone choosing to. That is the silently-shortened set the route
 * refuses unknown ids to prevent, arriving through the front door instead.
 *
 * So the list is the UNION of active sites and whatever is currently granted,
 * and a granted site that is no longer active is drawn and labelled rather than
 * omitted. Unticking it is then a decision somebody made.
 */

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  Drawer,
  Empty,
  Field,
  Loading,
  Pill,
  Toast,
} from './components/index.ts';
import {
  type Option,
  type SiteRef,
  bodyFor,
  diff,
  headline,
  isDirty,
  label,
  mergeOptions,
} from './user-sites.ts';

interface GrantsResponse {
  userId: string;
  role: string;
  sites: SiteRef[];
}

interface SitesResponse {
  sites: SiteRef[];
}

export function UserSitesDrawer({
  userId,
  email,
  role,
  onClose,
  onSaved,
}: {
  userId: string;
  email: string;
  role: string;
  onClose: () => void;
  /** Lets the Users table re-render its summary without refetching everything. */
  onSaved?: (codes: string[]) => void;
}): React.JSX.Element {
  const [options, setOptions] = useState<Option[] | null>(null);
  /** The set as the server last confirmed it. The baseline every diff is against. */
  const [saved, setSaved] = useState<Set<string> | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'ok' | 'fault' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      const [grants, all] = await Promise.all([
        api.get<GrantsResponse>(`/api/users/${userId}/sites`),
        api.get<SitesResponse>('/api/sites'),
      ]);

      const granted = new Set(grants.sites.map((s) => s.id));

      setOptions(mergeOptions(grants.sites, all.sites));
      setSaved(granted);
      setPicked(new Set(granted));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * The diff, by name. This is the screen's actual subject.
   *
   * Computed against `saved` rather than against the initial render, so it stays
   * correct after a save without a refetch — and so a second edit in the same
   * drawer diffs from what is now stored rather than from what was there when
   * the drawer opened.
   */
  const change = useMemo(
    () =>
      saved === null || options === null
        ? { adding: [], removing: [] }
        : diff(saved, picked, options),
    [picked, saved, options],
  );
  const { adding, removing } = change;

  const dirty = isDirty(change);
  const emptying = picked.size === 0;

  const visible = useMemo(() => {
    if (options === null) return [];
    const q = filter.trim().toLowerCase();
    if (q === '') return options;
    return options.filter((o) => label(o).toLowerCase().includes(q));
  }, [options, filter]);

  const save = useCallback(async () => {
    setBusy(true);
    try {
      const result = await api.put<{ userId: string; sites: string[] }>(
        `/api/users/${userId}/sites`,
        bodyFor(picked),
      );

      /*
       * Re-baseline from the REPLY, not from what we sent. The reply is the set
       * the database now holds, read back after the write — so if the two ever
       * disagree, the screen shows the database's answer and the diff above
       * immediately reads non-empty instead of quietly claiming success.
       */
      await load();
      setConfirming(false);
      onSaved?.(result.sites);
      setToast({
        message:
          result.sites.length === 0
            ? `${email} can now see no sites in the portal.`
            : `${email} can now see ${result.sites.length} site${result.sites.length === 1 ? '' : 's'}.`,
        tone: 'ok',
      });
    } catch (err) {
      // The server's refusals carry the reasoning — the non-client one names the
      // role and says why, which is better than anything this screen could say.
      setToast({
        message: err instanceof ApiError ? err.message : String(err),
        tone: 'fault',
      });
    } finally {
      setBusy(false);
    }
  }, [picked, userId, email, load, onSaved]);

  const toggle = (id: string): void => {
    setConfirming(false);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Portal sites"
      subtitle={`${email} · ${role} · portal only; staff roles see every site`}
    >
      {toast !== null && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}

      {error !== null && <Banner tone="fault">{error}</Banner>}

      {options === null || saved === null ? (
        <Loading rows={6} cols={1} label="Loading sites" />
      ) : (
        <>
          {/*
            🔴 Stated before the list, not after it. An operator who opened
            this drawer to add one site should not have to scroll a list of
            187 checkboxes to discover it is about to remove four.
          */}
          {/*
            The tone tracks REMOVAL, not merely "unsaved". Granting somebody a
            site is the safe direction and colouring it like a hazard would
            spend the warning on the common case — by the time it matters, on
            the screen that once emptied an ACL silently, nobody would be
            reading it any more.
          */}
          {dirty ? (
            <Banner tone={removing.length > 0 ? 'warn' : 'info'} title={headline(change)}>
              {removing.length > 0 && <div>Removing: {removing.join(', ')}</div>}
              {adding.length > 0 && <div className="muted">Adding: {adding.join(', ')}</div>}
            </Banner>
          ) : (
            <Banner
              tone="info"
              title={
                saved.size === 0
                  ? 'Sees no sites'
                  : `Sees ${saved.size} site${saved.size === 1 ? '' : 's'}`
              }
            >
              No unsaved changes.
            </Banner>
          )}

          <Field
            id="site-filter"
            label="Find a site"
            hint={`${visible.length} of ${options.length} shown`}
          >
            <input
              id="site-filter"
              type="search"
              value={filter}
              placeholder="code or name"
              onChange={(e) => setFilter(e.target.value)}
            />
          </Field>

          {visible.length === 0 ? (
            <Empty title="No site matches that" body="Clear the filter to see them all." />
          ) : (
            <ul className="site-grants">
              {visible.map((o) => {
                const id = `grant-${o.id}`;
                return (
                  <li key={o.id}>
                    <input
                      id={id}
                      type="checkbox"
                      checked={picked.has(o.id)}
                      disabled={busy}
                      onChange={() => toggle(o.id)}
                    />
                    {/* htmlFor rather than wrapping — see Field's docstring on
                        why a nested control in a label misbehaves here. */}
                    <label htmlFor={id}>
                      {label(o)}
                      {o.retired && (
                        <>
                          {' '}
                          <Pill
                            state="retired"
                            reason="Retired, and still granted. Unticking is permanent — a retired site cannot be granted again from here."
                          >
                            retired
                          </Pill>
                        </>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="row">
            {emptying && dirty ? (
              confirming ? (
                <>
                  {/*
                    The second guard. The wording is the customer's
                    consequence, not the schema's — an empty ACL means they
                    see NOTHING, and the route's comment is emphatic that it
                    must never be read as "all sites".
                  */}
                  <span className="small">
                    {email} will see nothing at all in the portal. Reversible here.
                  </span>
                  <Button variant="danger" busy={busy} onClick={() => void save()}>
                    Confirm — remove all access
                  </Button>
                  <Button variant="ghost" disabled={busy} onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="danger" disabled={busy} onClick={() => setConfirming(true)}>
                  Remove all access…
                </Button>
              )
            ) : (
              <Button variant="primary" busy={busy} disabled={!dirty} onClick={() => void save()}>
                {dirty ? 'Save changes' : 'Saved'}
              </Button>
            )}
            <Button
              variant="ghost"
              disabled={busy || !dirty}
              onClick={() => {
                setPicked(new Set(saved));
                setConfirming(false);
              }}
            >
              Revert
            </Button>
          </div>
        </>
      )}
    </Drawer>
  );
}
